import { useTranslation } from 'react-i18next';

import type { UserRef } from '@/shared/api/types';
import { cn } from '@/shared/lib/cn';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';

import { Avatar, type AvatarSize } from './Avatar';

/** Overlap ≈ 20% of the avatar size: enough to read as a stack while keeping both initials visible. */
const OVERLAP: Record<AvatarSize, string> = {
  xs: '-space-x-0.5',
  sm: '-space-x-1',
  md: '-space-x-1.5',
  lg: '-space-x-2',
  xl: '-space-x-3',
  '2xl': '-space-x-4',
};

const MORE_SIZE: Record<AvatarSize, string> = {
  xs: 'size-5 text-[0.5625rem]',
  sm: 'size-6 text-[0.625rem]',
  md: 'size-8 text-[0.6875rem]',
  lg: 'size-10 text-xs',
  xl: 'size-14 text-sm',
  '2xl': 'size-20 text-base',
};

export interface AvatarStackProps {
  users: Pick<UserRef, 'id' | 'fullName' | 'avatarUrl'>[];
  /** Max avatars before collapsing the rest into "+N". */
  max?: number;
  size?: AvatarSize;
  className?: string;
}

/** Overlapping avatars with a "+N" bubble; hovering shows everybody's names. */
export function AvatarStack({ users, max = 4, size = 'sm', className }: AvatarStackProps) {
  const { t } = useTranslation('shared');
  if (users.length === 0) return null;
  const visible = users.slice(0, max);
  const hidden = users.length - visible.length;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn('inline-flex items-center', OVERLAP[size], className)}
          role="group"
          aria-label={t('avatarStack.label', { count: users.length })}
          tabIndex={0}
        >
          {visible.map((user) => (
            <Avatar key={user.id} user={user} size={size} className="rounded-full ring-2 ring-background" />
          ))}
          {hidden > 0 && (
            <span
              className={cn(
                'relative inline-flex items-center justify-center rounded-full bg-muted font-semibold text-muted-foreground tabular-nums ring-2 ring-background',
                MORE_SIZE[size],
              )}
            >
              +{hidden}
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">
        {users
          .slice(0, 12)
          .map((u) => u.fullName)
          .join(', ')}
        {users.length > 12 && ` ${t('avatarStack.andMore', { count: users.length - 12 })}`}
      </TooltipContent>
    </Tooltip>
  );
}
